/**
 * Deterministic, pure representation resolver.
 * Contract: docs/doar-core/DOARSPACE_UNIVERSAL_WORKSPACE_CONTRACT.md #6, #10.
 *
 * Combines the backend-mirrored file capability tiers (utils/fileTypeMap.js)
 * with the editor capability registry (editorCapabilityRegistry.js) into one
 * normalized decision. It does not call any API and does not know about
 * React - callers (WorkspaceSession, UI) own the asset-fetch/poll loop.
 *
 * Resolution order is deliberate: file capability/kind is identified first,
 * so a processing/failed/source-only asset still keeps its known
 * workspaceMode/editorKind (e.g. a processing GLB stays workspaceMode "3D",
 * editorKind "model" while representationStatus is "processing") rather than
 * losing that context the moment the asset isn't in a terminal ready state.
 * Only representationStatus itself is decided by asset lifecycle status when
 * one applies - it is always the authoritative override for status, never
 * for kind/mode. Upload success is never automatically "ready to edit": a
 * SOURCE_ONLY file always resolves to representationStatus "source-only"
 * regardless of the asset's own status.
 */
import { getFileInfo, getFileInfoFromName } from "../../utils/fileTypeMap";
import { getEditorAdapter } from "./editorCapabilityRegistry";
import { WORKSPACE_MODES } from "./workspaceMode";

export const REPRESENTATION_STATUS = Object.freeze({
  EDITABLE: "editable",
  PREVIEWABLE: "previewable",
  // A real, usable final-delivery representation (e.g. USDZ for AR Quick
  // Look) that is not loaded/edited by any in-browser workspace editor. Not
  // "source-only" - it has a genuine current use, just not editing.
  NATIVE_OUTPUT: "native-output",
  PROCESSING: "processing",
  SOURCE_ONLY: "source-only",
  FAILED: "failed",
});

const CATEGORY_TO_KIND = Object.freeze({
  image: "image",
  video: "video",
  audio: "audio",
  model3d: "model",
  cad_bim: "cad-source",
  pointcloud: "pointcloud-source",
});

const KIND_TO_LEGACY_DIMENSION = Object.freeze({
  board: "2D",
  image: "2D",
  pdf: "2D",
  document: "2D",
  drawing: "2D",
  reference: "2D",
  video: "2.5D",
  audio: "2.5D",
  model: "3D",
  "model-native-output": "3D",
  "cad-source": "3D",
  "pointcloud-source": "3D",
  geo: "3.5D",
  "immersive-output": "4D",
});

const PROCESSING_ASSET_STATUSES = new Set(["uploading", "processing"]);
const SOURCE_ONLY_ASSET_STATUSES = new Set(["unsupported", "reference_only"]);
const FAILED_ASSET_STATUSES = new Set(["failed"]);

function resolveFileInfo(asset) {
  if (!asset) return null;
  if (asset.extension) return getFileInfo(asset.extension.startsWith(".") ? asset.extension : `.${asset.extension}`);
  if (asset.name) return getFileInfoFromName(asset.name);
  return null;
}

function categoryToKind(fileInfo) {
  if (!fileInfo) return null;
  if (fileInfo.category === "document") return fileInfo.ext === ".pdf" ? "pdf" : "document";
  // USDZ is DIRECT/model3d in fileTypeMap.js but browserReady:false - it is
  // ModelSpace's UPLOAD_ONLY set, not its three.js RENDERABLE set. Keep it
  // out of the editable "model" kind.
  if (fileInfo.category === "model3d" && fileInfo.ext === ".usdz") return "model-native-output";
  return CATEGORY_TO_KIND[fileInfo.category] || null;
}

function suggestNextAction(representationStatus) {
  switch (representationStatus) {
    case REPRESENTATION_STATUS.EDITABLE: return "open-in-editor";
    case REPRESENTATION_STATUS.PREVIEWABLE: return "open-preview";
    case REPRESENTATION_STATUS.NATIVE_OUTPUT: return "offer-native-delivery";
    case REPRESENTATION_STATUS.PROCESSING: return "wait-for-processing";
    case REPRESENTATION_STATUS.SOURCE_ONLY: return "show-source-only-state";
    case REPRESENTATION_STATUS.FAILED:
    default:
      return "show-failed-state";
  }
}

/**
 * @param {object} input
 * @param {object} [input.asset] - shape: { _id, status, extension, name, ... }
 * @param {string} [input.requestedMode] - "2D" | "3D"
 * @param {string} [input.requestedEditor] - editor adapter id
 * @returns {object} normalized decision
 */
export function resolveRepresentation({ asset = null, requestedMode = null, requestedEditor = null } = {}) {
  if (!asset) {
    return buildDecision({
      asset: null,
      requestedMode,
      requestedEditor,
      representationStatus: REPRESENTATION_STATUS.FAILED,
      editorEntry: null,
      reason: "no_asset",
    });
  }

  // 1-2. Identify file capability/kind and its editor/mode first, so lifecycle
  // status (step 3) never has to erase already-known format context.
  const fileInfo = resolveFileInfo(asset);
  const kind = categoryToKind(fileInfo);
  const editorEntry = kind ? getEditorAdapter(kind) : null;

  // 3. Asset lifecycle status is the authoritative override for STATUS only.
  const assetStatus = String(asset.status || "").toLowerCase();
  if (asset.metadata?.packageImport?.status === 'incomplete' || asset.metadata?.packageRegistrationFailed) return buildDecision({ asset, requestedMode, requestedEditor, representationStatus: REPRESENTATION_STATUS.FAILED, editorEntry, reason: 'incomplete_model_package' });

  if (PROCESSING_ASSET_STATUSES.has(assetStatus)) {
    return buildDecision({ asset, requestedMode, requestedEditor, representationStatus: REPRESENTATION_STATUS.PROCESSING, editorEntry });
  }
  if (FAILED_ASSET_STATUSES.has(assetStatus)) {
    return buildDecision({ asset, requestedMode, requestedEditor, representationStatus: REPRESENTATION_STATUS.FAILED, editorEntry });
  }
  if (SOURCE_ONLY_ASSET_STATUSES.has(assetStatus)) {
    return buildDecision({ asset, requestedMode, requestedEditor, representationStatus: REPRESENTATION_STATUS.SOURCE_ONLY, editorEntry });
  }

  // No lifecycle override - resolve purely from file capability. An
  // unrecognized extension truly has no kind/mode to offer.
  if (!fileInfo || fileInfo.capability === "REJECTED") {
    return buildDecision({
      asset,
      requestedMode,
      requestedEditor,
      representationStatus: REPRESENTATION_STATUS.FAILED,
      editorEntry,
      reason: "unrecognized_or_rejected_file",
    });
  }

  if (fileInfo.capability === "SOURCE_ONLY") {
    return buildDecision({ asset, requestedMode, requestedEditor, representationStatus: REPRESENTATION_STATUS.SOURCE_ONLY, editorEntry });
  }

  // DIRECT or REAL_CONVERSION with an already-"ready" asset (the backend
  // only marks a REAL_CONVERSION asset ready once conversion succeeded).
  if (editorEntry?.status === "editable") {
    return buildDecision({ asset, requestedMode, requestedEditor, representationStatus: REPRESENTATION_STATUS.EDITABLE, editorEntry });
  }
  if (editorEntry?.status === "previewable") {
    return buildDecision({ asset, requestedMode, requestedEditor, representationStatus: REPRESENTATION_STATUS.PREVIEWABLE, editorEntry });
  }
  if (editorEntry?.status === "output-only") {
    return buildDecision({ asset, requestedMode, requestedEditor, representationStatus: REPRESENTATION_STATUS.NATIVE_OUTPUT, editorEntry });
  }

  // Known file, but no working editor/preview/output registered for its kind yet.
  return buildDecision({ asset, requestedMode, requestedEditor, representationStatus: REPRESENTATION_STATUS.SOURCE_ONLY, editorEntry });
}

function buildDecision({ asset, requestedMode, requestedEditor, representationStatus, editorEntry, reason = null }) {
  const kind = editorEntry?.kind || null;
  const workspaceMode = editorEntry?.mode || null;
  const legacyDimension = kind ? KIND_TO_LEGACY_DIMENSION[kind] || null : null;
  const matchesRequest =
    (!requestedMode || requestedMode === workspaceMode) &&
    (!requestedEditor || requestedEditor === editorEntry?.editorId);

  return {
    ok: representationStatus !== REPRESENTATION_STATUS.FAILED,
    representationStatus,
    workspaceMode,
    legacyDimension,
    editorKind: kind,
    capabilities: editorEntry?.capabilities || [],
    asset,
    requestedMode: requestedMode || null,
    requestedEditor: requestedEditor || null,
    matchesRequest,
    suggestedNextAction: suggestNextAction(representationStatus),
    reason,
  };
}

export { WORKSPACE_MODES };
